<!--
  - Copyright (c) Interactive Information R & D (I2RD) LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - I2RD LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with I2RD.
  -->

<template>
  <v-dialog
    v-model="dialog"
    width="500px"
    @keydown.esc="userCanceled"
    @click:outside="userCanceled"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" v-bind="attrs" v-on="on">
        Onboard New Vendor
      </v-btn>
    </template>
    <v-card class="pa-4">
      <v-card-title class="pt-0">
        <slot name="title">Onboard New Vendor</slot>
      </v-card-title>

      <v-form ref="form" v-model="validForm" @keyup.native.enter="save">
        <v-skeleton-loader type="card" v-if="loading" />
        <v-card-text v-else>
          <v-text-field
            v-model="newVendor.name"
            :rules="[rules.required()]"
            dense
            filled
            label="Vendor Name*"
          ></v-text-field>
          <v-text-field
            v-model="newVendor.email"
            :rules="[rules.required(), rules.email]"
            dense
            filled
            label="Vendor Email*"
          ></v-text-field>
          <v-text-field
            v-model="newVendor.phone"
            :rules="[rules.phoneNumber]"
            dense
            filled
            label="Vendor Phone"
          ></v-text-field>
          <v-select
            v-if="hasSubclients"
            :items="subclients"
            :rules="[rules.required()]"
            item-text="name"
            item-value="id"
            v-model="newVendor.facility"
            dense
            filled
            placeholder="Please Select"
            label="Facility*"
          ></v-select>
          <v-select
            :items="vendorCategories"
            v-model="newVendor.category"
            item-value="value"
            dense
            filled
            placeholder="Please Select"
            label="Category"
          ></v-select>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            class="mr-2 cancel-btn"
            color="colorPtRoyal"
            outlined
            @click="userCanceled"
          >
            Cancel
          </v-btn>
          <!-- Can't use type="submit", replaces # with ?# in the URL for some reason -->
          <v-btn color="primary" @click="save" :disabled="loading || saving">
            <v-icon v-if="!saving">mdi-check</v-icon>
            <v-progress-circular
              v-else
              :size="20"
              :width="2"
              indeterminate
            ></v-progress-circular>
            Begin Onboarding
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as rules from "@/services/rules";
import { get, post } from "@/services/request";
import {
  currentClientAPI,
  subclientsForParentIdAPI,
  vendorCategoryAPI,
  workflowsAPI
} from "@/services/api";
import hasNotifications from "@/mixins/has-notifications";

export default {
  name: "OnboardNewVendorDialogButton",
  components: {},
  mixins: [hasNotifications],

  data() {
    return {
      dialog: false,
      newVendor: {
        name: null,
        email: null,
        phone: null,
        client: null,
        facility: null,
        category: null,
        createdByClient: true
      },
      rules,
      validForm: false,
      loading: true,
      saving: false,
      client: {},
      subclients: [],
      vendorCategories: []
    };
  },
  computed: {
    hasSubclients() {
      return Array.isArray(this.subclients) && this.subclients.length > 0;
    }
  },
  created() {
    this.loadData();
  },
  watch: {
    dialog(newValue) {
      if (!newValue) {
        Promise.resolve(this.$refs.form.reset()).then(() =>
          this.setNewVendorDefaultData()
        );
      }
    }
  },
  methods: {
    userCanceled() {
      //This is also called on "implied cancels" like clicking outside
      this.dialog = false;
      //Matches com.i2rd.vendorproof.util.analytics.event.AnalyticEventType.CLICKED_CANCEL_ONBOARDING_BUTTON
      this.logEvent("Clicked Cancel Onboarding Button");
    },
    save() {
      //Matches com.i2rd.vendorproof.util.analytics.event.AnalyticEventType.CLICKED_BEGIN_ONBOARDING_BUTTON
      this.logEvent("Clicked Begin Onboarding Button");
      if (this.$refs.form.validate()) {
        this.saving = true;
        post(workflowsAPI, this.newVendor)
          .then(() => {
            this.dialog = false;
            this.$emit("saved");
          })
          .catch(() => {
            this.showMessage({
              text:
                "Failed to begin onboarding, please double check the fields and try again, or contact us for help",
              mode: "error"
            });
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    loadData() {
      this.loading = true;
      const getClientPromise = get(currentClientAPI.getUrl()).then(response => {
        this.client = response.data;
      });
      const getSubclientPromise = getClientPromise.then(() =>
        get(subclientsForParentIdAPI.getUrl(this.client.id)).then(response => {
          this.subclients = response.data;
        })
      );
      const getCategoriesPromise = getClientPromise.then(() =>
        get(vendorCategoryAPI.getUrl(this.client.id)).then(response => {
          this.vendorCategories = response.data;
        })
      );

      Promise.all([
        getClientPromise,
        getSubclientPromise,
        getCategoriesPromise
      ]).then(() => {
        this.loading = false;
        this.setNewVendorDefaultData();
      });
    },
    setNewVendorDefaultData() {
      this.newVendor.client = this.client.id;
      if (
        Array.isArray(this.vendorCategories) &&
        this.vendorCategories.length >= 1
      ) {
        this.newVendor.category = this.vendorCategories[0].value; //Most likely No Category Assigned
      }
    }
  }
};
</script>

<style lang="scss" scoped>
//Color should match the component 'color' prop, but isn't for some reason
.v-btn.v-btn--outlined.cancel-btn {
  border-color: $color-pt-royal;
}
</style>
